import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, switchMap, tap, of } from 'rxjs';
import { GroupService } from './group.service';
import { PracticeSettingsStore } from './practice-settings-store.service';

@Injectable({
  providedIn: 'root'
})
export class NoteTypeService {
  private apiUrl = 'api';
  private noteTypesSubject = new BehaviorSubject<any>(null);
  noteTypes$ = this.noteTypesSubject.asObservable();
  private noteTypeTemplatesSubject = new BehaviorSubject<any[]>([]);
  noteTypeTemplates$ = this.noteTypeTemplatesSubject.asObservable();

  groupId?: string;

  constructor(
    private http: HttpClient,
    private groupService: GroupService,
    private settingsStore?: PracticeSettingsStore,
  ) {
    // Load note types when group changes
    this.groupService.selectedGroup$.pipe(
      switchMap(selectedGroup => {
        if (selectedGroup) {
          this.groupId = selectedGroup.id;
          return this.getNoteTypesByGroup(true);
        }
        return EMPTY;
      })
    ).subscribe();

    // Load templates when group changes (separate stream to avoid nested subscriptions)
    this.groupService.selectedGroup$.pipe(
      switchMap(selectedGroup => {
        if (selectedGroup && selectedGroup.groupSpeciality) {
          return this.getNoteTypeTemplatesBySpecialty(selectedGroup.groupSpeciality, true);
        }
        this.noteTypeTemplatesSubject.next([]);
        return of([]);
      })
    ).subscribe();
  }

  getWardRoundNoteType(): string {
    return this.noteTypesSubject.getValue().filter((nt: any) => nt.label === 'Ward Round')[0];
  }

  /** `fromKeptCopy`: on a practice switch, the device's kept copy while it is current (PracticeSettingsStore). */
  getNoteTypesByGroup(fromKeptCopy = false): Observable<any> {
    const token = localStorage.getItem('token');
    const request = this.http.get(`${this.apiUrl}/note-types/group/${this.groupId}`, { headers: { Authorization: `Bearer ${token}` } });
    const source = fromKeptCopy && this.settingsStore ? this.settingsStore.read(this.groupId!, 'noteTypes', () => request) : request;
    return source
      .pipe(
        tap(results => { this.noteTypesSubject.next(results); })
      );
  }

  deleteNoteType(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.delete(`${this.apiUrl}/note-types/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
  }

  updateNoteTypes(noteTypes: any[]): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put(`${this.apiUrl}/note-types`, { groupId: this.groupId, noteTypes }, {
      headers: { Authorization: `Bearer ${token}` }
    })
  }

  areHeadingsStrings(headings: any[]): boolean {
    return headings.some((heading) => typeof heading === 'string');
  }

  convertHeadingsToObjects(headings: string[]): { text: string; subheadings: any[] }[] {
    return headings.map((heading) => (typeof heading === 'string' ? {
      text: heading,
      subheadings: [],
    } : heading));
  }

  getNoteTypeTemplatesBySpecialty(specialty: string, fromKeptCopy = false): Observable<any[]> {
    const token = localStorage.getItem('token');
    const request = this.http.get<any[]>(`${this.apiUrl}/note-type-templates/by-specialty/${encodeURIComponent(specialty)}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const source = fromKeptCopy && this.settingsStore ? this.settingsStore.read(this.groupId!, `noteTypeTemplates:${specialty}`, () => request) : request;
    return source.pipe(
      tap(templates => {
        // Only include active templates
        const activeTemplates = (templates || []).filter((t: any) => t.isActive);
        this.noteTypeTemplatesSubject.next(activeTemplates);
      })
    );
  }
}
